import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * Low-poly wireframe schematics — hand-built with imperative Three.js so
 * JSX instrumentation (visual-edits babel plugin) does not collide with
 * react-three-fiber's prop reconciler. Each canvas renders a single
 * schematic that auto-rotates and responds to page scroll.
 */

const HYPER_CYAN = 0x00F0FF;
const WARN_AMBER = 0xFFB800;

/**
 * Reusable setup — creates scene, camera, renderer, handles rAF loop and
 * resize. Returns objects for caller to populate and tweak per frame.
 */
function useThreeScene(mountRef, drawSceneFactory) {
  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;
    const width = el.clientWidth;
    const height = el.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 0, 3.4);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    el.appendChild(renderer.domElement);

    const { root, update, dispose } = drawSceneFactory(scene);

    // scroll-linked rotation
    const scrollState = { y: 0, x: 0 };
    const onScroll = () => {
      scrollState.y = window.scrollY * 0.002;
      scrollState.x = window.scrollY * 0.0008;
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // resize
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w === 0 || h === 0) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    ro.observe(el);

    // render loop
    let rafId;
    const clock = new THREE.Clock();
    const tick = () => {
      const dt = clock.getDelta();
      update && update(dt, scrollState, root);
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener('scroll', onScroll);
      dispose && dispose();
      el.removeChild(renderer.domElement);
      renderer.dispose();
      scene.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) {
          const m = Array.isArray(o.material) ? o.material : [o.material];
          m.forEach((mm) => mm.dispose && mm.dispose());
        }
      });
    };
  }, [mountRef, drawSceneFactory]);
}

// ── Zero-Trust Shield — hex outline + concentric rings + icosahedron core
function buildShield(color) {
  return (scene) => {
    const root = new THREE.Group();

    // hex outline
    const hexPts = [];
    for (let i = 0; i <= 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.PI / 2;
      hexPts.push(new THREE.Vector3(Math.cos(a) * 1.1, Math.sin(a) * 1.3, 0));
    }
    const hex = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(hexPts),
      new THREE.LineBasicMaterial({ color })
    );
    root.add(hex);

    // concentric rings
    [0.55, 0.8, 1.05].forEach((r, i) => {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(r, 0.012, 8, 64),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.35 + i * 0.2 })
      );
      ring.rotation.x = Math.PI / 2;
      root.add(ring);
    });

    // core
    const core = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.28, 0),
      new THREE.MeshBasicMaterial({ color, wireframe: true })
    );
    root.add(core);

    scene.add(root);
    return {
      root,
      update: (dt, scrollState) => {
        root.rotation.y += dt * 0.35;
        root.rotation.x = scrollState.x;
      },
    };
  };
}

// ── AI Neural Mesh — icosahedron + 10 satellite nodes ─────────────────
function buildNeural(color) {
  return (scene) => {
    const root = new THREE.Group();

    const shell = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1, 1),
      new THREE.MeshBasicMaterial({ color, wireframe: true })
    );
    root.add(shell);

    const phi = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < 10; i++) {
      const y = 1 - (i / 9) * 2;
      const r = Math.sqrt(1 - y * y);
      const theta = phi * i;
      const node = new THREE.Mesh(
        new THREE.SphereGeometry(0.06, 8, 8),
        new THREE.MeshBasicMaterial({ color: i % 3 === 0 ? WARN_AMBER : color })
      );
      node.position.set(Math.cos(theta) * r * 1.3, y * 1.3, Math.sin(theta) * r * 1.3);
      root.add(node);
    }

    scene.add(root);
    return {
      root,
      update: (dt, scrollState) => {
        root.rotation.x += dt * 0.25;
        root.rotation.y += dt * 0.4;
        root.rotation.z = scrollState.y;
      },
    };
  };
}

// ── Mechanical Assembly — nested gears + axle ─────────────────────────
function buildMechanical(color) {
  return (scene) => {
    const root = new THREE.Group();
    root.rotation.x = -0.3;

    const outer = new THREE.Group();
    const outerRing = new THREE.Mesh(
      new THREE.TorusGeometry(1.15, 0.02, 8, 64),
      new THREE.MeshBasicMaterial({ color })
    );
    outerRing.rotation.x = Math.PI / 2;
    outer.add(outerRing);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const tooth = new THREE.Mesh(
        new THREE.BoxGeometry(0.08, 0.04, 0.04),
        new THREE.MeshBasicMaterial({ color })
      );
      tooth.position.set(Math.cos(a) * 1.15, 0, Math.sin(a) * 1.15);
      outer.add(tooth);
    }
    root.add(outer);

    const inner = new THREE.Group();
    const innerRing = new THREE.Mesh(
      new THREE.TorusGeometry(0.7, 0.02, 8, 48),
      new THREE.MeshBasicMaterial({ color })
    );
    innerRing.rotation.x = Math.PI / 2;
    inner.add(innerRing);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const tooth = new THREE.Mesh(
        new THREE.BoxGeometry(0.06, 0.04, 0.06),
        new THREE.MeshBasicMaterial({ color: i === 0 ? WARN_AMBER : color })
      );
      tooth.position.set(Math.cos(a) * 0.7, 0, Math.sin(a) * 0.7);
      inner.add(tooth);
    }
    root.add(inner);

    const axle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, 0.5, 12),
      new THREE.MeshBasicMaterial({ color })
    );
    axle.rotation.x = Math.PI / 2;
    root.add(axle);

    scene.add(root);
    return {
      root,
      update: (dt, scrollState) => {
        outer.rotation.z += dt * 0.4;
        inner.rotation.z -= dt * 0.6;
        root.rotation.y = scrollState.y * 2;
      },
    };
  };
}

// ── Public wrappers ────────────────────────────────────────────────────
function SchematicCanvas({ factory, height = 180 }) {
  const ref = useRef(null);
  useThreeScene(ref, factory);
  return <div ref={ref} style={{ width: '100%', height, pointerEvents: 'none' }} aria-hidden="true" />;
}

export function ShieldSchematic({ color = HYPER_CYAN, height }) {
  const factory = React.useMemo(() => buildShield(color), [color]);
  return <SchematicCanvas factory={factory} height={height} />;
}
export function NeuralMesh({ color = HYPER_CYAN, height }) {
  const factory = React.useMemo(() => buildNeural(color), [color]);
  return <SchematicCanvas factory={factory} height={height} />;
}
export function MechanicalAssembly({ color = HYPER_CYAN, height }) {
  const factory = React.useMemo(() => buildMechanical(color), [color]);
  return <SchematicCanvas factory={factory} height={height} />;
}
